// Following practices from https://javascript.info/custom-errors

import { ValidatedResponse } from "braintree";

export class BraintreeWebhookError extends Error {
  /**
   * Indicates a generic occurred while parsing a Braintree webhook call.
   * This is to be used either when no additional error detail is available, or for catching inheriting exceptions.
   */
  constructor(message?: string) {
    super(message);
    this.name = "BraintreeWebhookError";
  }
}

export class NoBraintreeEmailError extends BraintreeWebhookError {
  /**
   * Signals that no customer email was found in a Braintree webhook. This could be anywhere in the received message,
   * and could be the result of a parent object being null or undefined.
   * @param subscriptionId The Braintree subscription ID for which an associated email address cannot be found.
   */
  constructor(subscriptionId: string) {
    super(`Failed to find an email associated with subscription ID "${subscriptionId}".`);
    this.name = "NoBraintreeEmailError";
  }
}

export class UnmappedBraintreeNotificationKindError extends BraintreeWebhookError {
  /**
   * Signals that a webhook received from Braintree hasn't been mapped to an internal GC message type.
   */
  constructor(notificationKind: string) {
    super(`Could not find mapped GC message type for Braintree notification of kind "${notificationKind}".`);
    this.name = "UnmappedBraintreeNotificationKindError";
  }
}

export class BraintreeGatewayError extends Error {
  braintreeResponse: ValidatedResponse<any>;

  constructor(braintreeResponse: ValidatedResponse<any>, message?: string) {
    super(message);
    this.name = "BraintreeGatewayError";
    this.braintreeResponse = braintreeResponse;
  }
}

export class CustomerSubscriptionNotFoundError extends Error {
  constructor(subscriptionKey: string) {
    super(`Customer subscription with key ${subscriptionKey} not found.`);
    this.name = "CustomerSubscriptionNotFoundError";
  }
}

export class CustomerAlreadySubscribedError extends Error {
  constructor() {
    super("Customer is already subscribed");
    this.name = "CustomerAlreadySubscribedError";
  }
}

export class SubscriptionPauseInvalidError extends Error {
  constructor(subscriptionStatus: string) {
    super(`Subscription with status ${subscriptionStatus} cannot be paused`);
    this.name = "SubscriptionPauseInvalidError";
  }
}

export class ClientServerError extends Error {
  cause: any;
  statusCode: number | undefined;

  constructor(causalError: any, statusCode?: number) {
    super();
    this.name = "ClientServerError";
    this.cause = causalError;
    this.statusCode = statusCode;
  }
}

export class NoResponseBodyError extends Error {
  responseData: any;

  constructor(responseData: any) {
    super();
    this.name = "NoResponseBodyError";
    this.responseData = responseData;
  }
}

export class AppError extends Error {
  message: string;
  statusCode: number;

  constructor(message: string, statusCode: number) {
    super();
    this.name = "AppError";
    this.message = message;
    this.statusCode = statusCode;
  }
}
